import { useQueryClient, type UseQueryOptions } from 'react-query'

import {
  apiPaths,
  handleError,
  queryKeys,
  validateStatus,
} from 'api/kinerja/penilik-pegawai/config'
import { useQueryApi, useMutationApi } from 'utils/api'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { AxiosError } from 'axios'
import type { CommonResponse } from 'types/api'
import type {
  PraktikKinerjaItem,
  SatuanPendidikanItem,
  RHKOptionType,
  ResponsePerencanaanSummary,
  ResponsePerencanaanSubmission,
  Perencanaan,
  PengembanganKompetensiIndikatorOptions,
  PengembanganKompetensiKategori,
  PengembanganKompetensiKegiatan,
  RequestPerencanaanBody,
  RequestPerencanaanPerilakuKerjaBody,
  RequestMulaiPelaksanaanBody,
  ResetSKPPhase,
} from 'types/kinerja-penilik-pegawai'

// Praktik Kinerja Options
export function useAPIGetPerencanaanRHKOptions({
  type,
  opts,
}: {
  type?: RHKOptionType
  opts?: UseQueryOptions<CommonResponse<PraktikKinerjaItem[]>>
}) {
  return useQueryApi<CommonResponse<PraktikKinerjaItem[]>>(
    `${apiPaths.perencanaan}/rhk/${type}`,
    {
      queryConfig: {
        ...opts,
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
      },
      queryKey: [queryKeys.API_GET_PRAKTIK_KINERJA_OPTIONS, type],
    }
  )
}

export function useAPIGetPerencanaanSatuanPendidikanOptions({
  periodeId,
  rhkId,
  opts,
}: {
  periodeId?: string
  rhkId?: string
  opts?: UseQueryOptions<CommonResponse<SatuanPendidikanItem[]>>
}) {
  return useQueryApi<CommonResponse<SatuanPendidikanItem[]>>(
    `${apiPaths.perencanaan}/${periodeId}/rapor-satuan-pendidikan/${rhkId}`,
    {
      queryConfig: {
        ...opts,
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
      },
      queryKey: [queryKeys.API_GET_SATUAN_PENDIDIKAN_OPTIONS, periodeId, rhkId],
    }
  )
}

// Pengembangan Kompetensi Options
export function useAPIGetIndikatorKompetensiOptions() {
  return useQueryApi<PengembanganKompetensiIndikatorOptions>(
    `${apiPaths.pengembanganKompetensi}/indikator`,
    {
      queryConfig: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
      },
      queryKey: [queryKeys.API_GET_PENGEMBANGAN_KOMPETENSI_INDIKATOR],
    }
  )
}
export function useAPIGetKompetensiKategoriOptions() {
  return useQueryApi<CommonResponse<PengembanganKompetensiKategori[]>>(
    `${apiPaths.pengembanganKompetensi}/kategori`,
    {
      queryConfig: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
      },
      queryKey: [queryKeys.API_GET_PENGEMBANGAN_KOMPETENSI_KATEGORI],
    }
  )
}
export function useAPIGetKompetensiKegiatanOptions({
  kategoriId,
  ...opts
}: UseQueryOptions<CommonResponse<PengembanganKompetensiKegiatan[]>> & {
  kategoriId?: string
}) {
  return useQueryApi<CommonResponse<PengembanganKompetensiKegiatan[]>>(
    `${apiPaths.pengembanganKompetensi}/kategori/${kategoriId}/kegiatan`,
    {
      queryConfig: {
        ...opts,
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
      },
      queryKey: [
        queryKeys.API_GET_PENGEMBANGAN_KOMPETENSI_KEGIATAN,
        kategoriId,
      ],
    }
  )
}

export function useAPIGetPerencanaanSummary({
  periodId,
  ...options
}: UseQueryOptions<ResponsePerencanaanSummary> & { periodId: string }) {
  return useQueryApi<ResponsePerencanaanSummary>(
    apiPaths.summaryPerencanaan(periodId),
    {
      queryKey: [queryKeys.API_GET_PERENCANAAN_SUMMARY, periodId],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: true,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIGetPerencanaan({
  periodId,
  ...options
}: UseQueryOptions<Perencanaan> & { periodId: string }) {
  return useQueryApi<Perencanaan>(apiPaths.perencanaanByPeriod(periodId), {
    queryKey: [queryKeys.API_GET_PERENCANAAN, periodId],
    axiosConfig: {
      validateStatus,
    },
    queryConfig: {
      useErrorBoundary: true,
      ...options,
    },
    errorHandlerFn: handleError,
  })
}

export function useAPISubmitPerencanaan<T>(apiPath: string) {
  const { period } = usePeriodContext()
  const queryClient = useQueryClient()

  return useMutationApi<ResponsePerencanaanSubmission, AxiosError, T>(apiPath, {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PERENCANAAN_SUMMARY,
          period.id,
        ])
      },
    },
  })
}

export function useAPISubmitPerilakuKerja() {
  return useAPISubmitPerencanaan<RequestPerencanaanPerilakuKerjaBody>(
    `${apiPaths.perencanaan}/perilaku-kerja`
  )
}

export function useAPIAjukanPerencanaan(periodId: string) {
  const { period } = usePeriodContext()
  const queryClient = useQueryClient()

  return useMutationApi<
    ResponsePerencanaanSubmission,
    AxiosError,
    RequestPerencanaanBody
  >(() => apiPaths.ajukanPerencanaan(periodId), {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PERENCANAAN_SUMMARY,
          period.id,
        ])
      },
    },
  })
}

export function useAPIMulaiPelaksanaan() {
  const { period } = usePeriodContext()
  const queryClient = useQueryClient()

  return useMutationApi<
    ResponsePerencanaanSubmission,
    AxiosError,
    RequestMulaiPelaksanaanBody
  >(() => apiPaths.mulaiPelaksanaan, {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PERENCANAAN_SUMMARY,
          period.id,
        ])
      },
    },
  })
}

export function useApiResetSkp({ phase }: { phase: ResetSKPPhase }) {
  const { period } = usePeriodContext()
  const queryClient = useQueryClient()

  return useMutationApi<{ message: string }, AxiosError, {}>(
    () => apiPaths.resetSkp(period.id, phase),
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        onSettled() {
          queryClient.invalidateQueries([
            queryKeys.API_GET_PERENCANAAN,
            period.id,
          ])

          queryClient.invalidateQueries([
            queryKeys.API_GET_PERENCANAAN_SUMMARY,
            period.id,
          ])
        },
      },
    }
  )
}
